import { useEffect, useMemo, useRef, useState } from "react";

import { Frame, Toast as ToastEl, AbandonModal } from "@/components/Frame";
import { SummaryRail } from "@/components/SummaryRail";
import { CartReview } from "@/screens/CartReview";
import { Identity } from "@/screens/Identity";
import { Redeem } from "@/screens/Redeem";
import { Checkout } from "@/screens/Checkout";
import { Success, type RedeemStatus } from "@/screens/Success";
import {
  WIZARD_FLOWS,
  type FlowId,
  type ScreenId,
} from "@/constants";
import {
  startWizard,
  postIdentity,
  postRedeem,
  postCheckout,
  getSession,
  parseWizardTerminal,
  setBearerToken,
  fetchMe,
  type CartSnapshot,
} from "@/lib/api";
import { scopedStorage, STORAGE_KEYS } from "@/lib/storage";
import { CURRENT_BRAND } from "@/brand/config";
import { credentialErrorMessage } from "@/lib/credential";
import { SetTemporaryPinModal } from "@/components/SetTemporaryPinModal";

type ToastKind = "ok" | "warn" | "err" | "info";
type Toast = { msg: string; kind: ToastKind } | null;

type FormState = {
  email: string;
  password: string;
  nombre: string;
  rfc: string;
  telefono: string;
  terms: boolean;
};

const EMPTY_FORM: FormState = {
  email: "",
  password: "",
  nombre: "",
  rfc: "",
  telefono: "",
  terms: false,
};

// SE-159ONB-READY: redeem's POST is async (backend queues provisioning), so
// success can only be claimed after an exact terminal confirmation from
// getSession — never from the redeem response alone (not even a truthy
// r.provisioned: corrective 1 found a synthetic `provisioned: "false"` string
// — truthy, wrong value — skipping the GET entirely under the first cut of
// this fix). Bounded poll: ~8 tries at 2.5s is a ~20s window before we fall
// back to manual "unconfirmed" retry rather than spinning forever or
// guessing a result.
const REDEEM_POLL_INTERVAL_MS = 2500;
const REDEEM_POLL_MAX_ATTEMPTS = 8;

// Once one of these is reached it is final: no later-arriving read (automatic
// poll tick or manual retry) may downgrade it. "pending"/"unconfirmed" are
// not final — a later read may still resolve them into one of these.
const TERMINAL_PHASES: ReadonlySet<RedeemStatus> = new Set(["prepared", "failed"]);

type WizardCheck = "prepared" | "pending" | "unconfirmed" | "failed";

async function checkWizardStatus(sessionId: string): Promise<WizardCheck> {
  try {
    await getSession(sessionId);
    return "pending";
  } catch (e) {
    const terminal = parseWizardTerminal(e);
    if (terminal?.status === "provisioned") return "prepared";
    if (terminal?.status === "failed" || terminal?.status === "abandoned") return "failed";
    return "unconfirmed";
  }
}

// URLSearchParams.get() ya decodifica una vez. Cuando el code/quote llega
// doble-encodeado (p.ej. %2520 -> %20 tras el primer decode) hay que decodificar
// otra vez, o el backend responde INVALID_CODE. Sólo re-decodificamos si aún
// queda una secuencia %XX, para no corromper valores limpios (un UUID no lleva %).
function decodeParam(raw: string | null): string | null {
  if (!raw) return null;
  let v = raw;
  if (/%[0-9A-Fa-f]{2}/.test(v)) {
    try {
      v = decodeURIComponent(v);
    } catch {
      /* valor mal formado: lo dejamos tal cual y que el backend lo rechace */
    }
  }
  return v;
}

function resolveEntry(): {
  initialFlow: FlowId;
  initialCode: string | null;
  initialQuoteToken: string | null;
} {
  const url = new URL(window.location.href);
  // ADR-CMRC-013: ?credential= es el param canónico polimórfico; ?code= se
  // mantiene por compat con URLs ya emitidas (mint CLI, emails).
  const code =
    decodeParam(url.searchParams.get("credential")) ||
    decodeParam(url.searchParams.get("code"));
  const quoteToken = decodeParam(url.searchParams.get("quote"));
  if (code) return { initialFlow: "code", initialCode: code, initialQuoteToken: null };
  if (quoteToken)
    return { initialFlow: "cart", initialCode: null, initialQuoteToken: quoteToken };
  return { initialFlow: "cart", initialCode: null, initialQuoteToken: null };
}

export default function App() {
  const { initialFlow, initialCode, initialQuoteToken } = useMemo(resolveEntry, []);

  const [flow, setFlow] = useState<FlowId>(initialFlow);
  const [step, setStep] = useState(0);
  const screens = WIZARD_FLOWS[flow].screens;
  const steps = WIZARD_FLOWS[flow].steps;
  const current: ScreenId = screens[step];

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [cart, setCart] = useState<CartSnapshot | null>(null);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [resumed, setResumed] = useState(false);

  const [payMode, setPayMode] = useState<"annual" | "monthly">("annual");
  const [paying, setPaying] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  // SE-159ONB-READY: "idle" = redeem flow not reached yet (also the cart
  // flow's default, which never calls submitRedeem). Everything else is
  // driven by an exact terminal confirmation, never assumed from the POST.
  const [redeemPhase, setRedeemPhaseState] = useState<"idle" | RedeemStatus>("idle");
  const [statusChecking, setStatusChecking] = useState(false);
  const redeemInFlightRef = useRef(false);
  const redeemEpochRef = useRef(0);
  const pollTimerRef = useRef<number | null>(null);
  // Mirrors redeemPhase for synchronous reads inside async callbacks (state
  // itself is only visible after a re-render). Used to detect "someone else
  // already reached a final phase while I was awaiting" (corrective 2).
  const redeemPhaseRef = useRef<"idle" | RedeemStatus>("idle");
  // Only one getSession read (automatic poll tick or manual retry) may be in
  // flight at a time — corrective 2 found an automatic tick and a manual
  // retry racing, where the automatic tick's stale rejection landed after
  // the retry's terminal result and overwrote it back to "unconfirmed".
  const readInFlightRef = useRef(false);
  // SE-159ONB-READY corrective 2: src/main.tsx mounts App in React.StrictMode,
  // whose dev double-invoke can start bootstrapFromCode/Quote twice
  // concurrently. Only the call still matching this counter when it
  // resolves may apply its result (including the bearer token, now set
  // here rather than inside startWizard) — a superseded call must not
  // touch sessionId/cart/bearer/loading state at all, success or failure.
  const bootstrapEpochRef = useRef(0);

  const setRedeemPhase = (p: "idle" | RedeemStatus) => {
    redeemPhaseRef.current = p;
    setRedeemPhaseState(p);
  };

  const clearRedeemPoll = () => {
    if (pollTimerRef.current !== null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const scheduleRedeemPoll = (sid: string, epoch: number, attempt: number) => {
    clearRedeemPoll();
    pollTimerRef.current = window.setTimeout(async () => {
      if (epoch !== redeemEpochRef.current) return;
      if (readInFlightRef.current) {
        // A manual retry (or another tick) already owns the one allowed
        // in-flight read; come back shortly instead of racing it.
        scheduleRedeemPoll(sid, epoch, attempt);
        return;
      }
      readInFlightRef.current = true;
      let result: WizardCheck;
      try {
        result = await checkWizardStatus(sid);
      } finally {
        // SE-159ONB-READY corrective 2: only clear the lock if we still own
        // it. If a session swap happened while this read was in flight, the
        // `[sessionId]` effect already reset the ref for the new session
        // (or a newer read already re-acquired it) — clearing unconditionally
        // here could release a lock that a newer, still-in-flight read holds.
        if (epoch === redeemEpochRef.current) readInFlightRef.current = false;
      }
      if (epoch !== redeemEpochRef.current) return;
      if (TERMINAL_PHASES.has(redeemPhaseRef.current as RedeemStatus)) return;
      if (result === "pending") {
        if (attempt + 1 >= REDEEM_POLL_MAX_ATTEMPTS) {
          setRedeemPhase("unconfirmed");
        } else {
          scheduleRedeemPoll(sid, epoch, attempt + 1);
        }
      } else {
        setRedeemPhase(result);
      }
    }, REDEEM_POLL_INTERVAL_MS);
  };

  async function retryRedeemStatus() {
    if (!sessionId || readInFlightRef.current) return;
    clearRedeemPoll(); // cancel any pending future tick; we're checking right now
    const epoch = redeemEpochRef.current;
    readInFlightRef.current = true;
    setStatusChecking(true);
    let result: WizardCheck;
    try {
      result = await checkWizardStatus(sessionId);
    } finally {
      if (epoch === redeemEpochRef.current) readInFlightRef.current = false;
    }
    if (epoch !== redeemEpochRef.current) {
      // SE-159ONB-READY corrective 2: do NOT setStatusChecking(false) here —
      // this retry belongs to an abandoned session; the `[sessionId]` effect
      // already reset statusChecking for whatever session is current now,
      // and a current retry may legitimately still be checking.
      return;
    }
    setStatusChecking(false);
    if (TERMINAL_PHASES.has(redeemPhaseRef.current as RedeemStatus)) return;
    if (result === "pending") {
      setRedeemPhase("pending");
      scheduleRedeemPoll(sessionId, epoch, 0);
    } else {
      setRedeemPhase(result);
    }
  }

  // New session (fresh bootstrap or code swap) invalidates any poll in
  // flight for the previous session — never let a stale response land.
  // SE-159ONB-READY corrective 2: also reset redeeming/redeemInFlightRef —
  // a StrictMode-driven session swap while a redeem POST was still pending
  // left `redeeming` stuck true forever, because the old POST's own
  // `finally` only cleared it when its epoch still matched (correctly
  // skipping the new session), but nothing else ever cleared it either.
  useEffect(() => {
    redeemEpochRef.current += 1;
    clearRedeemPoll();
    readInFlightRef.current = false;
    redeemInFlightRef.current = false;
    setRedeemPhase("idle");
    setStatusChecking(false);
    setRedeeming(false);
  }, [sessionId]);

  useEffect(() => {
    return () => {
      redeemEpochRef.current += 1;
      clearRedeemPoll();
      bootstrapEpochRef.current += 1;
    };
  }, []);

  const [codeOpen, setCodeOpen] = useState(Boolean(initialCode));
  const [code, setCode] = useState(initialCode || "");
  const [codeErr, setCodeErr] = useState("");
  const [validating, setValidating] = useState(false);

  const [cartLoading, setCartLoading] = useState(false);
  const [cartError, setCartError] = useState<string | null>(null);
  const [emptyCart, setEmptyCart] = useState(!initialQuoteToken && !initialCode);

  const [modal, setModal] = useState(false);
  const [toast, setToast] = useState<Toast>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // ARES-CORE-001: PIN temporal / force-change.
  // - showPinModal lo dispara: (a) el axios interceptor cuando cualquier
  //   response trae mustChangePin / pin.forceChange, (b) un fetchMe explícito
  //   al recibir bearer.
  // - pinIsSet=false => modal en variante "primer PIN" (sin currentPin).
  // - pinIsSet=true && forceChange=true => modal en variante "PIN temporal".
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinIsSet, setPinIsSet] = useState(true);

  useEffect(() => {
    const onMust = () => setShowPinModal(true);
    window.addEventListener("synapta:must-change-pin", onMust);
    return () => window.removeEventListener("synapta:must-change-pin", onMust);
  }, []);

  const fire = (msg: string, kind: ToastKind = "info") => {
    setToast({ msg, kind });
    window.clearTimeout((fire as { _t?: number })._t);
    (fire as { _t?: number })._t = window.setTimeout(() => setToast(null), 2800);
  };

  const set = <K extends keyof FormState>(k: K, val: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: val }));
  const blur = (k: keyof FormState) => setTouched((p) => ({ ...p, [k]: true }));

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [flow, step]);

  useEffect(() => {
    if (initialQuoteToken) {
      void bootstrapFromQuote(initialQuoteToken);
    } else if (initialCode) {
      void bootstrapFromCode(initialCode);
    }
  }, [initialQuoteToken, initialCode]);

  async function bootstrapFromQuote(token: string) {
    bootstrapEpochRef.current += 1;
    const myBootstrap = bootstrapEpochRef.current;
    setCartLoading(true);
    setCartError(null);
    try {
      const r = await startWizard({ quoteToken: token });
      if (myBootstrap !== bootstrapEpochRef.current) return; // superseded/closed/unmounted
      setBearerToken(r.token);
      setSessionId(r.sessionId);
      setCart(r.cart);
      setEmptyCart(false);
      scopedStorage.set(STORAGE_KEYS.sessionId, r.sessionId);
    } catch (e) {
      if (myBootstrap !== bootstrapEpochRef.current) return;
      setCartError(toErr(e));
    } finally {
      if (myBootstrap === bootstrapEpochRef.current) setCartLoading(false);
    }
  }

  async function bootstrapFromCode(theCode: string) {
    bootstrapEpochRef.current += 1;
    const myBootstrap = bootstrapEpochRef.current;
    setValidating(true);
    setCodeErr("");
    try {
      const r = await startWizard({ redemptionCode: theCode });
      if (myBootstrap !== bootstrapEpochRef.current) return; // superseded/closed/unmounted
      setBearerToken(r.token);
      setSessionId(r.sessionId);
      setCart(r.cart);
      setFlow("code");
      setStep(0);
      scopedStorage.set(STORAGE_KEYS.sessionId, r.sessionId);
    } catch (e) {
      if (myBootstrap !== bootstrapEpochRef.current) return;
      setCodeErr(toErr(e) || "Código inválido o expirado.");
      setFlow("cart");
      setStep(0);
    } finally {
      if (myBootstrap === bootstrapEpochRef.current) setValidating(false);
    }
  }

  async function validateInlineCode() {
    if (code.trim().length < 4) return;
    await bootstrapFromCode(code.trim());
  }

  async function continueFromCart() {
    if (sessionId) {
      setStep(1);
      return;
    }
    fire("Tu cotización no se ha cargado todavía.", "warn");
  }

  async function submitIdentity() {
    if (!sessionId) {
      fire("Sesión no inicializada.", "err");
      return;
    }
    try {
      await postIdentity(sessionId, {
        email: form.email,
        password: form.password,
        nombre: form.nombre,
        rfc: form.rfc || undefined,
        telefono: form.telefono || undefined,
      });
      setResumed(false);
      // ARES-CORE-001: post-identity ya tenemos bearer. Consultar /me para
      // saber si hay PIN seteado y si está marcado force_change. El
      // interceptor también dispara el evento si la respuesta lo trae, pero
      // este fetch es el primer momento canónico para evaluar el estado.
      try {
        const me = await fetchMe();
        if (me.pin) {
          setPinIsSet(me.pin.isSet);
          if (me.pin.forceChange || !me.pin.isSet) setShowPinModal(true);
        }
      } catch {
        // /me puede no estar disponible (backend pre-ARES-CORE-001 deploy);
        // dejamos al interceptor la responsabilidad si llega un response
        // con la flag.
      }
      setStep(step + 1);
    } catch (e) {
      const msg = toErr(e);
      if (msg.includes("PAYMENT_PENDING") || msg.includes("pendiente")) {
        setResumed(true);
        fire("Encontramos una cuenta pendiente con este correo.", "warn");
      } else {
        fire(msg || "No pudimos crear tu cuenta.", "err");
      }
    }
  }

  async function submitRedeem() {
    // Single-flight guard via ref, not state: state only updates after a
    // re-render, so a rapid double-click can fire submitRedeem twice before
    // the button's disabled={redeeming} prop repaints.
    if (!sessionId || redeemInFlightRef.current) return;
    redeemInFlightRef.current = true;
    setRedeeming(true);
    const epoch = redeemEpochRef.current;
    try {
      await postRedeem(sessionId, { credential: code.trim(), email: form.email });
      if (epoch !== redeemEpochRef.current) return; // session replaced mid-flight
      // SE-159ONB-READY corrective 1: the redeem response's `provisioned`
      // field is never trusted for UI state (a synthetic `"false"` string —
      // truthy, wrong value — proved that shortcut unsafe). Only an exact
      // getSession terminal confirmation (see checkWizardStatus) may claim
      // the company is prepared.
      setRedeemPhase("pending");
      fire("Registramos tu solicitud. Estamos preparando tu empresa…", "info");
      scheduleRedeemPoll(sessionId, epoch, 0);
      setStep(step + 1);
    } catch (e) {
      if (epoch !== redeemEpochRef.current) return;
      // ADR-CMRC-013: mensajes contextuales por errorCode del resolver.
      const contextual = credentialErrorMessage(toErrCode(e));
      fire(contextual || toErr(e) || "No pudimos activar tu cuenta.", "err");
    } finally {
      // SE-159ONB-READY corrective 2: gate both, not just `redeeming` — an
      // unconditional clear here let a session swap during this POST leave
      // the NEW session's redeemInFlightRef falsely appearing free/taken
      // depending on ordering. The `[sessionId]` effect already resets both
      // for whatever session is current; a stale POST settling later must
      // not touch either.
      if (epoch === redeemEpochRef.current) {
        redeemInFlightRef.current = false;
        setRedeeming(false);
      }
    }
  }

  async function submitPay() {
    if (!sessionId) return;
    setPaying(true);
    try {
      // Checkout Pro: el backend devuelve { intentId, initPoint } (preference o
      // preapproval). Redirigimos al init_point hospedado por MercadoPago; el
      // resultado vuelve por back_urls (st=success|pending|failure) — ver C-3/C-4.
      // "Pago único anual" => subscription ausente (createPreference, one-time).
      // "Suscripción mensual" => "monthly" (createPreapproval recurrente).
      const { initPoint } = await postCheckout(sessionId, {
        subscription: payMode === "monthly" ? "monthly" : undefined,
      });
      window.location.assign(initPoint);
    } catch (e) {
      fire(toErr(e) || "Pago rechazado. Intenta con otra tarjeta.", "err");
    } finally {
      setPaying(false);
    }
  }

  function onClose() {
    setModal(true);
  }
  function confirmClose() {
    redeemEpochRef.current += 1;
    clearRedeemPoll();
    bootstrapEpochRef.current += 1;
    setBearerToken(null);
    scopedStorage.remove(STORAGE_KEYS.sessionId);
    window.location.assign(CURRENT_BRAND.branding?.marketingUrl || "/");
  }

  const railTotals = {
    subtotal: cart?.subtotal || "0",
    tax: cart?.tax || "0",
    total: cart?.total || "0",
  };
  const railPkg =
    flow === "code" && cart
      ? {
          name: "Tu paquete",
          product:
            cart.items?.[0]?.title || cart.items?.[0]?.sku || "Acceso digital",
          vence: undefined,
        }
      : null;

  return (
    <Frame
      steps={Array.from(steps)}
      currentStep={step}
      onClose={onClose}
      contentRef={contentRef}
      rail={
        <SummaryRail
          flow={flow}
          empty={emptyCart}
          items={cart?.items || []}
          totals={railTotals}
          pkg={railPkg}
        />
      }
    >
      {current === "cart" && (
        <CartReview
          loading={cartLoading}
          error={!!cartError}
          empty={emptyCart}
          items={(cart?.items as Array<{
            id?: string;
            sku: string;
            title?: string;
            meta?: string;
            lineTotal: string;
            icon?: string;
          }>) || []}
          totals={{
            subtotal: cart?.subtotal || "0",
            tax: cart?.tax || "0",
            total: cart?.total || "0",
          }}
          codeOpen={codeOpen}
          code={code}
          setCode={setCode}
          codeErr={codeErr}
          onToggleCode={() => setCodeOpen((v) => !v)}
          onValidateCode={validateInlineCode}
          onRetry={() => initialQuoteToken && bootstrapFromQuote(initialQuoteToken)}
          onContinue={continueFromCart}
          validating={validating}
        />
      )}
      {current === "identity" && (
        <Identity
          v={form}
          set={set}
          touched={touched}
          blur={blur}
          emailCollision={resumed}
          onContinue={submitIdentity}
        />
      )}
      {current === "pay" && (
        <Checkout
          mode={payMode}
          setMode={setPayMode}
          total={cart?.total || "0"}
          paying={paying}
          onPay={submitPay}
        />
      )}
      {current === "redeem" && (
        <Redeem
          code={code}
          redeeming={redeeming}
          onRedeem={submitRedeem}
          onChangeCode={() => {
            setFlow("cart");
            setStep(0);
            setCodeOpen(true);
            setCode("");
          }}
        />
      )}
      {current === "success" && (
        <Success
          name={form.nombre}
          email={form.email}
          loginUrl={
            CURRENT_BRAND.branding?.loginUrl ||
            import.meta.env.VITE_LOGIN_URL ||
            "/"
          }
          status={redeemPhase === "idle" ? "active" : redeemPhase}
          checking={statusChecking}
          onRetryStatus={retryRedeemStatus}
        />
      )}

      {modal && (
        <AbandonModal onCancel={() => setModal(false)} onConfirm={confirmClose} />
      )}
      {toast && <ToastEl toast={toast} />}
      {showPinModal && (
        <SetTemporaryPinModal
          requireCurrentPin={pinIsSet}
          onSuccess={() => {
            setShowPinModal(false);
            setPinIsSet(true);
            fire("PIN actualizado.", "ok");
          }}
        />
      )}
    </Frame>
  );
}

function toErrCode(e: unknown): string | null {
  if (typeof e === "object" && e !== null) {
    const anyE = e as { response?: { data?: { error_code?: string } } };
    return anyE.response?.data?.error_code || null;
  }
  return null;
}

function toErr(e: unknown): string {
  if (typeof e === "object" && e !== null) {
    const anyE = e as { response?: { data?: { error_code?: string; message?: string } }; message?: string };
    return (
      anyE.response?.data?.message ||
      anyE.response?.data?.error_code ||
      anyE.message ||
      ""
    );
  }
  return String(e);
}
